import { openapiClient, unwrapResult } from '@/api/openapi-client';
import { useRestaurantStore } from '@/stores/restaurant.store';
import type {
  Printer,
  PrinterStatusEvent,
  PrintJob,
  PrintJobStatus,
  PrintPreview,
  PrintPreviewKind,
  PrintRouting,
  PrintSettings,
  RegisterPrinterBody,
  UpdatePrinterBody,
  UpdatePrintSettingsBody,
  UpsertPrintRoutingBody,
} from '@restaurant/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const printKeys = {
  printers: (restaurantId: string) => ['print', restaurantId, 'printers'] as const,
  routing: (restaurantId: string) => ['print', restaurantId, 'routing'] as const,
  jobs: (restaurantId: string, printerId?: string, status?: string) =>
    ['print', restaurantId, 'jobs', printerId ?? 'all', status ?? 'all'] as const,
  statusEvents: (restaurantId: string, printerId: string) =>
    ['print', restaurantId, 'status-events', printerId] as const,
  settings: (restaurantId: string) => ['print', restaurantId, 'settings'] as const,
  preview: (restaurantId: string, kind: PrintPreviewKind) =>
    ['print', restaurantId, 'preview', kind] as const,
};

// ─── Printers ────────────────────────────────────────────────────────────────

export function usePrinters() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: printKeys.printers(restaurantId),
    queryFn: async () => {
      return unwrapResult<Printer[]>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/printers', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
    // Derived status (ONLINE/OFFLINE/ERROR) + queued counts go stale on their
    // own - keep the list fresh while the operator watches it.
    refetchInterval: 15_000,
  });
}

export function usePrinterStatusEvents(printerId: string | null) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: printKeys.statusEvents(restaurantId, printerId ?? ''),
    queryFn: async () => {
      return unwrapResult<PrinterStatusEvent[]>(
        await openapiClient.GET(
          '/api/restaurants/{restaurantId}/printers/{printerId}/status-events',
          { params: { path: { restaurantId, printerId: printerId ?? '' }, query: { limit: 50 } } },
        ),
      );
    },
    enabled: !!restaurantId && !!printerId,
    // The reconciler appends new transitions each minute; keep the timeline live.
    refetchInterval: 30_000,
  });
}

export function useRegisterPrinter() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: RegisterPrinterBody) =>
      unwrapResult<Printer>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/printers', {
          params: { path: { restaurantId } },
          body,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printKeys.printers(restaurantId) });
      queryClient.invalidateQueries({ queryKey: printKeys.routing(restaurantId) });
    },
  });
}

export function useUpdatePrinter() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ printerId, data }: { printerId: string; data: UpdatePrinterBody }) =>
      unwrapResult<Printer>(
        await openapiClient.PATCH('/api/restaurants/{restaurantId}/printers/{printerId}', {
          params: { path: { restaurantId, printerId } },
          body: data,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printKeys.printers(restaurantId) });
      queryClient.invalidateQueries({ queryKey: printKeys.routing(restaurantId) });
    },
  });
}

/**
 * Send a test ticket to one printer. Invalidates jobs (the test shows up in the
 * job list) and printers (its queued count moves) so the operator can watch it
 * go QUEUED → PRINTED without a manual refresh.
 */
export function useTestPrint() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (printerId: string) =>
      unwrapResult<PrintJob>(
        await openapiClient.POST(
          '/api/restaurants/{restaurantId}/printers/{printerId}/test-print',
          { params: { path: { restaurantId, printerId } } },
        ),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print', restaurantId, 'jobs'] });
      queryClient.invalidateQueries({ queryKey: printKeys.printers(restaurantId) });
    },
  });
}

// ─── Routing ─────────────────────────────────────────────────────────────────

export function usePrintRouting() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: printKeys.routing(restaurantId),
    queryFn: async () => {
      return unwrapResult<PrintRouting>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/print-routing', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useUpsertRouting() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpsertPrintRoutingBody) =>
      unwrapResult<PrintRouting>(
        await openapiClient.PUT('/api/restaurants/{restaurantId}/print-routing', {
          params: { path: { restaurantId } },
          body,
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: printKeys.routing(restaurantId) });
      queryClient.invalidateQueries({ queryKey: printKeys.printers(restaurantId) });
    },
  });
}

// ─── Print behaviour ("what to print automatically") ─────────────────────────

export function usePrintSettings() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: printKeys.settings(restaurantId),
    queryFn: async () => {
      return unwrapResult<PrintSettings>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/print-settings', {
          params: { path: { restaurantId } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

export function useUpdatePrintSettings() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdatePrintSettingsBody) =>
      unwrapResult<PrintSettings>(
        await openapiClient.PATCH('/api/restaurants/{restaurantId}/print-settings', {
          params: { path: { restaurantId } },
          body,
        }),
      ),
    // Seed the cache from the response so the toggle reflects the saved state
    // immediately without a refetch round-trip.
    onSuccess: (settings) => {
      queryClient.setQueryData(printKeys.settings(restaurantId), settings);
      // Text size / category toggles change the rendered ticket, so drop every
      // cached preview kind and let the visible one refetch.
      queryClient.invalidateQueries({ queryKey: ['print', restaurantId, 'preview'] });
    },
  });
}

/** Sample ticket rendered server-side with the current print settings. */
export function usePrintPreview(kind: PrintPreviewKind) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: printKeys.preview(restaurantId, kind),
    queryFn: async () => {
      return unwrapResult<PrintPreview>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/print-preview', {
          params: { path: { restaurantId }, query: { kind } },
        }),
      );
    },
    enabled: !!restaurantId,
  });
}

// ─── Jobs ────────────────────────────────────────────────────────────────────

export function usePrintJobs(filters: { printerId?: string; status?: PrintJobStatus }) {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  return useQuery({
    queryKey: printKeys.jobs(restaurantId, filters.printerId, filters.status),
    queryFn: async () => {
      return unwrapResult<PrintJob[]>(
        await openapiClient.GET('/api/restaurants/{restaurantId}/print-jobs', {
          params: {
            path: { restaurantId },
            query: { printerId: filters.printerId, status: filters.status, limit: 100 },
          },
        }),
      );
    },
    enabled: !!restaurantId,
    refetchInterval: 15_000,
  });
}

/**
 * Print an online order's kitchen ticket from the order screen (GitHub #255).
 *
 * Returns EVERY job the order routed to — one per station when the ticket is
 * split, so the caller can say how many tickets are coming. Resolves as soon as
 * the job is queued; the printer polls on its own schedule, so the copy must say
 * "queued", never "printed".
 */
export function usePrintOrderTicket() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) =>
      unwrapResult<PrintJob[]>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/print-order-ticket', {
          params: { path: { restaurantId } },
          body: { orderId },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print', restaurantId, 'jobs'] });
    },
  });
}

export function useReprintJob() {
  const restaurantId = useRestaurantStore((s) => s.selectedRestaurantId) ?? '';
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) =>
      unwrapResult<PrintJob>(
        await openapiClient.POST('/api/restaurants/{restaurantId}/print-jobs/{jobId}/reprint', {
          params: { path: { restaurantId, jobId } },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print', restaurantId, 'jobs'] });
    },
  });
}
